import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, ChevronRight, X, Plus, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import FileUploader from "@/components/FileUploader";
import { eur } from "@/lib/constants";
import { toast } from "sonner";

const STEPS = ["Fichier", "Format actuel", "Nouveaux formats", "Options", "Récapitulatif"];

const PRINT_PRESETS = [
  { key: "A3", label: "A3", w: 297, h: 420 },
  { key: "A4", label: "A4", w: 210, h: 297 },
  { key: "A5", label: "A5", w: 148, h: 210 },
  { key: "A6", label: "A6", w: 105, h: 148 },
  { key: "custom", label: "Personnalisé", w: null, h: null },
];

const DIGITAL_PRESETS = [
  { key: "custom", label: "Personnalisé", w: null, h: null },
  { key: "1080x1350", label: "1080×1350 · Instagram vertical", w: 1080, h: 1350 },
  { key: "1080x1080", label: "1080×1080 · Carré réseaux", w: 1080, h: 1080 },
  { key: "1920x1080", label: "1920×1080 · Bannière écran", w: 1920, h: 1080 },
];

const LABELS = {
  channel: { print: "Impression", digital: "Numérique / écran" },
  dest: { imprimeur: "Imprimeur professionnel", perso: "Impression personnelle", digital: "Numérique / écran", unknown: "À définir" },
  composition_intent: {
    keep: "Conserver la composition actuelle",
    adjust: "Conserver l'esprit avec quelques ajustements",
    rework: "Retravailler la composition",
  },
  has_source: { yes: "Oui", no: "Non", unknown: "Je ne sais pas" },
};

function computePrice(formats) {
  const n = formats.length;
  const rv = formats.filter((f) => f.rv).length;
  let base = 0;
  if (n === 1) base = 19;
  else if (n === 2) base = 34;
  else if (n === 3) base = 47;
  else if (n >= 4) base = 47 + 13 * (n - 3);
  const surcharge = rv * 10;
  return { base, rv, surcharge, total: base + surcharge };
}

function shouldWarnProportions(currentW, currentH, target) {
  const cw = Number(currentW), ch = Number(currentH);
  if (!cw || !ch) return false;
  let tw = target.width, th = target.height;
  if (target.preset && target.preset !== "custom") {
    const pool = target.channel === "print" ? PRINT_PRESETS : DIGITAL_PRESETS;
    const p = pool.find((x) => x.key === target.preset);
    if (p?.w) { tw = p.w; th = p.h; }
  }
  if (!tw || !th) return false;
  const rc = cw / ch;
  const rt = tw / th;
  const ratio = rc > rt ? rc / rt : rt / rc;
  return ratio >= 1.8; // significant proportion shift
}

function StepDots({ step }) {
  return (
    <ol className="flex flex-wrap gap-x-4 gap-y-2 text-xs font-mono">
      {STEPS.map((s, i) => (
        <li key={i} className={`flex items-center gap-2 ${i === step ? "text-[#0E7C90]" : i < step ? "text-black" : "text-neutral-400"}`}>
          {i < step ? <Check size={14} /> : <span className="inline-block w-4 text-center">{i + 1}</span>}
          <span className="uppercase tracking-widest">{s}</span>
          {i < STEPS.length - 1 && <ChevronRight size={12} className="text-neutral-400" />}
        </li>
      ))}
    </ol>
  );
}

const emptyFormat = () => ({
  channel: "print", preset: "A5", width: "", height: "", unit: "mm",
  dest: "imprimeur", rv: false, verso_files: [],
});

export default function FormatConfigurator() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState([]);
  const [hasSource, setHasSource] = useState("");
  const [currentKnown, setCurrentKnown] = useState(false);
  const [current, setCurrent] = useState({ w: "", h: "", unit: "mm" });
  const [formats, setFormats] = useState([emptyFormat()]);
  const [hasSpecs, setHasSpecs] = useState(null);
  const [specsText, setSpecsText] = useState("");
  const [specsFiles, setSpecsFiles] = useState([]);
  const [compositionIntent, setCompositionIntent] = useState("keep");
  const [notes, setNotes] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const price = useMemo(() => computePrice(formats), [formats]);
  const hasImprimeur = formats.some((f) => f.dest === "imprimeur");

  const updateFormat = (i, patch) => setFormats((arr) => arr.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const removeFormat = (i) => setFormats((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));
  const addFormat = () => setFormats((arr) => [...arr, emptyFormat()]);

  const canNext = () => {
    if (step === 0) return files.length > 0 || hasSource; // upload OR at least answered source Q
    if (step === 1) return true; // optional
    if (step === 2) {
      if (formats.length < 1) return false;
      return formats.every((f) => {
        if (f.preset && f.preset !== "custom") return true;
        return Number(f.width) > 0 && Number(f.height) > 0;
      });
    }
    if (step === 3) return true;
    return true;
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!confirm) {
      toast.error("Merci de confirmer votre demande");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        file_ids: files.map((f) => f.id),
        has_source: hasSource || "unknown",
        current_known: currentKnown,
        current_width: current.w ? Number(current.w) : null,
        current_height: current.h ? Number(current.h) : null,
        current_unit: current.unit,
        formats: formats.map((f) => ({
          channel: f.channel,
          preset: f.preset,
          width: f.width ? Number(f.width) : null,
          height: f.height ? Number(f.height) : null,
          unit: f.unit || (f.channel === "digital" ? "px" : "mm"),
          dest: f.dest,
          rv: f.rv,
          verso_file_ids: (f.verso_files || []).map((x) => x.id),
        })),
        has_specs: hasSpecs,
        specs_text: specsText,
        specs_file_ids: specsFiles.map((f) => f.id),
        composition_intent: compositionIntent,
        notes,
        confirm: true,
      };
      const { data } = await api.post("/orders/format", payload);
      toast.success(`Demande enregistrée · ${data.reference}`);
      nav(`/espace/commande/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cs-cream py-14 min-h-[80vh]" data-testid="page-format-configurator">
      <div className="lg:hidden sticky top-[73px] z-30 bg-[#FAF8F5]/95 backdrop-blur border-y border-black/10 px-6 py-3 flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-500">Total en cours</div>
        <div className="font-serif text-2xl" data-testid="format-total-mobile">{eur(price.total)}</div>
      </div>

      <div className="max-w-[1180px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <div className="cs-eyebrow text-neutral-500">Configurateur · J'ai déjà mon projet</div>
          <h1 className="cs-h1 mt-3 text-4xl lg:text-5xl">Le mettre au bon format.</h1>
          <p className="mt-3 text-[15px] text-neutral-700 max-w-2xl">
            Adaptez un visuel existant à une ou plusieurs nouvelles dimensions. Fonds perdus techniques inclus lorsqu'ils sont nécessaires.
          </p>
          <div className="mt-6"><StepDots step={step} /></div>

          <div className="mt-8 cs-card p-6 sm:p-8" data-testid={`format-step-${step}`}>
            {step === 0 && (
              <div>
                <h2 className="cs-h3 text-2xl">Envoyez votre projet</h2>
                <p className="mt-2 text-sm text-neutral-600">Formats acceptés : PDF, PSD, JPG, PNG, TIFF, ZIP.</p>
                <div className="mt-4"><FileUploader files={files} setFiles={setFiles} testId="format-uploader" category="format_source" /></div>

                <div className="mt-8 border-t border-black/10 pt-6">
                  <div className="cs-eyebrow text-neutral-500">Disposez-vous du fichier source modifiable ?</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[["yes", "Oui"], ["no", "Non, seulement l'exporté"], ["unknown", "Je ne sais pas"]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setHasSource(v)}
                        className={`px-4 py-2 border text-sm ${hasSource === v ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                        data-testid={`source-${v}`}>{l}</button>
                    ))}
                  </div>
                  <p className="mt-3 text-[13px] text-neutral-600">
                    Un fichier source facilite certaines adaptations. Son absence n'empêche pas la réalisation.
                    <span className="text-black"> Aucun supplément appliqué.</span>
                  </p>
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 className="cs-h3 text-2xl">Format actuel</h2>
                <div className="mt-4 flex gap-2">
                  <button type="button" onClick={() => setCurrentKnown(true)}
                    className={`px-4 py-2 border text-sm ${currentKnown ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                    data-testid="curr-known">Je connais les dimensions</button>
                  <button type="button" onClick={() => setCurrentKnown(false)}
                    className={`px-4 py-2 border text-sm ${!currentKnown ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                    data-testid="curr-unknown">Je ne connais pas</button>
                </div>
                {currentKnown ? (
                  <div className="mt-4 grid sm:grid-cols-[1fr_1fr_120px] gap-3">
                    <div><label className="cs-eyebrow text-neutral-500">Largeur</label>
                      <input type="number" className="cs-input mt-2" value={current.w} onChange={(e) => setCurrent({ ...current, w: e.target.value })} data-testid="curr-w" /></div>
                    <div><label className="cs-eyebrow text-neutral-500">Hauteur</label>
                      <input type="number" className="cs-input mt-2" value={current.h} onChange={(e) => setCurrent({ ...current, h: e.target.value })} data-testid="curr-h" /></div>
                    <div><label className="cs-eyebrow text-neutral-500">Unité</label>
                      <select className="cs-input mt-2" value={current.unit} onChange={(e) => setCurrent({ ...current, unit: e.target.value })} data-testid="curr-unit">
                        <option value="mm">mm</option><option value="cm">cm</option><option value="px">px</option>
                      </select></div>
                  </div>
                ) : (
                  <p className="mt-3 text-[13px] text-neutral-600">Charbonier Studio vérifiera votre fichier — ce n'est pas bloquant.</p>
                )}
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="cs-h3 text-2xl">Quels formats souhaitez-vous obtenir ?</h2>
                <p className="mt-2 text-sm text-neutral-600">Ajoutez autant de déclinaisons que nécessaire. Le prix se met à jour automatiquement.</p>
                <div className="mt-6 space-y-4">
                  {formats.map((f, i) => {
                    const presets = f.channel === "print" ? PRINT_PRESETS : DIGITAL_PRESETS;
                    const warn = shouldWarnProportions(current.w, current.h, f);
                    return (
                      <div key={i} className="border border-black/15 bg-white p-4" data-testid={`fmt-block-${i}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-serif text-lg">Format {i + 1}</div>
                          {formats.length > 1 && (
                            <button type="button" onClick={() => removeFormat(i)} className="p-2 hover:text-[#0E7C90]" data-testid={`fmt-remove-${i}`} aria-label="Retirer">
                              <X size={16} />
                            </button>
                          )}
                        </div>
                        <div className="mt-3 grid sm:grid-cols-2 gap-2">
                          {["print", "digital"].map((ch) => (
                            <button key={ch} type="button" onClick={() => updateFormat(i, { channel: ch, preset: ch === "print" ? "A5" : "custom", unit: ch === "digital" ? "px" : "mm" })}
                              className={`px-3 py-2 border text-sm text-left ${f.channel === ch ? "border-[#0E7C90] bg-[#F5F1EC]" : "border-black/15"}`}
                              data-testid={`fmt-${i}-ch-${ch}`}>{LABELS.channel[ch]}</button>
                          ))}
                        </div>

                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {presets.map((p) => (
                            <button key={p.key} type="button" onClick={() => updateFormat(i, { preset: p.key, width: p.w || "", height: p.h || "" })}
                              className={`px-3 py-2 border text-sm text-left ${f.preset === p.key ? "border-[#0E7C90] bg-[#F5F1EC]" : "border-black/15"}`}
                              data-testid={`fmt-${i}-p-${p.key}`}>{p.label}</button>
                          ))}
                        </div>

                        {f.preset === "custom" && (
                          <div className="mt-3 grid grid-cols-[1fr_1fr_120px] gap-2">
                            <input type="number" placeholder="Largeur" className="cs-input" value={f.width} onChange={(e) => updateFormat(i, { width: e.target.value })} data-testid={`fmt-${i}-w`} />
                            <input type="number" placeholder="Hauteur" className="cs-input" value={f.height} onChange={(e) => updateFormat(i, { height: e.target.value })} data-testid={`fmt-${i}-h`} />
                            <select className="cs-input" value={f.unit} onChange={(e) => updateFormat(i, { unit: e.target.value })}>
                              <option value="mm">mm</option><option value="cm">cm</option><option value="px">px</option>
                            </select>
                          </div>
                        )}

                        <div className="mt-3">
                          <label className="cs-eyebrow text-neutral-500">Destination</label>
                          <div className="mt-2 grid sm:grid-cols-2 gap-2">
                            {["imprimeur", "perso", "digital", "unknown"].map((d) => (
                              <button key={d} type="button" onClick={() => updateFormat(i, { dest: d })}
                                className={`px-3 py-2 border text-sm text-left ${f.dest === d ? "border-[#0E7C90] bg-[#F5F1EC]" : "border-black/15"}`}
                                data-testid={`fmt-${i}-dest-${d}`}>{LABELS.dest[d]}</button>
                            ))}
                          </div>
                        </div>

                        {warn && (
                          <div className="mt-3 border border-[#0E7C90]/50 bg-[#0E7C90]/5 p-3 text-[13px] flex items-start gap-2">
                            <Info size={16} className="mt-0.5 text-[#0E7C90] shrink-0" />
                            <span>
                              Cette adaptation peut nécessiter une recomposition. Charbonier Studio vérifiera votre fichier —
                              <span className="text-black"> aucun supplément sans votre accord.</span>
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <button type="button" onClick={addFormat} className="mt-4 inline-flex items-center gap-2 text-sm border border-black/70 px-4 py-2 hover:bg-black hover:text-[#FAF8F5] transition-colors" data-testid="fmt-add">
                  <Plus size={14} /> Ajouter un format
                </button>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="cs-h3 text-2xl">Options d'adaptation</h2>

                <div className="mt-6">
                  <div className="cs-eyebrow text-neutral-500">Comment souhaitez-vous adapter votre visuel ?</div>
                  <div className="mt-2 grid sm:grid-cols-1 gap-2">
                    {Object.entries(LABELS.composition_intent).map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setCompositionIntent(v)}
                        className={`px-4 py-3 border text-sm text-left ${compositionIntent === v ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                        data-testid={`intent-${v}`}>
                        <span className="font-serif text-lg">{l}</span>
                      </button>
                    ))}
                  </div>
                  {compositionIntent === "rework" && (
                    <p className="mt-3 text-[13px] text-neutral-700 border-l-2 border-[#0E7C90] pl-3">
                      Votre demande peut nécessiter une prestation de recomposition graphique. Envoyez votre fichier :
                      Charbonier Studio vérifiera le travail nécessaire avant toute modification tarifaire.
                    </p>
                  )}
                </div>

                <div className="mt-8 border-t border-black/10 pt-6">
                  <div className="cs-eyebrow text-neutral-500">Recto-verso par format</div>
                  <p className="mt-2 text-[13px] text-neutral-600">+10 € par format recto-verso adapté.</p>
                  <div className="mt-3 space-y-3">
                    {formats.map((f, i) => (
                      <div key={i} className="border border-black/10 bg-white p-4">
                        <div className="flex items-center justify-between">
                          <span className="font-serif text-lg">Format {i + 1} · {f.channel === "print" ? f.preset : (f.preset === "custom" ? "Numérique perso" : f.preset)}</span>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => updateFormat(i, { rv: false })}
                              className={`px-3 py-1.5 text-sm border ${!f.rv ? "border-[#0E7C90] bg-[#F5F1EC]" : "border-black/15"}`}
                              data-testid={`fmt-${i}-rv-no`}>Recto seul</button>
                            <button type="button" onClick={() => updateFormat(i, { rv: true })}
                              className={`px-3 py-1.5 text-sm border ${f.rv ? "border-[#0E7C90] bg-[#F5F1EC]" : "border-black/15"}`}
                              data-testid={`fmt-${i}-rv-yes`}>Recto-verso · +10 €</button>
                          </div>
                        </div>
                        {f.rv && (
                          <div className="mt-3">
                            <div className="cs-eyebrow text-neutral-500">Fichier verso associé</div>
                            <div className="mt-2">
                              <FileUploader files={f.verso_files || []}
                                setFiles={(fn) => {
                                  const cur = f.verso_files || [];
                                  const nxt = typeof fn === "function" ? fn(cur) : fn;
                                  updateFormat(i, { verso_files: nxt });
                                }}
                                testId={`fmt-${i}-verso`} category={`format_${i + 1}_verso`} />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {hasImprimeur && (
                  <div className="mt-8 border-t border-black/10 pt-6">
                    <div className="cs-eyebrow text-neutral-500">Spécifications de votre imprimeur ?</div>
                    <div className="mt-2 flex gap-2">
                      <button type="button" onClick={() => setHasSpecs(true)}
                        className={`px-4 py-2 border text-sm ${hasSpecs === true ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                        data-testid="specs-yes">Oui</button>
                      <button type="button" onClick={() => setHasSpecs(false)}
                        className={`px-4 py-2 border text-sm ${hasSpecs === false ? "border-[#0E7C90] bg-white" : "border-black/15 bg-white/60"}`}
                        data-testid="specs-no">Non</button>
                    </div>
                    {hasSpecs && (
                      <div className="mt-4 space-y-3">
                        <textarea rows={4} className="cs-input" placeholder="Collez ou décrivez les indications reçues"
                          value={specsText} onChange={(e) => setSpecsText(e.target.value)} data-testid="specs-text" />
                        <FileUploader files={specsFiles} setFiles={setSpecsFiles} testId="specs-upload" category="printer_specs" />
                        <p className="text-[12px] text-neutral-600">
                          Si votre demande implique davantage qu'un simple changement de format, Charbonier Studio pourra vous orienter vers
                          « Rendre mon fichier conforme à l'imprimeur » à 29 €.
                          <span className="text-black"> Aucun supplément sans votre accord.</span>
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-8 border-t border-black/10 pt-6">
                  <label className="cs-eyebrow text-neutral-500">Une consigne particulière ? (facultatif)</label>
                  <textarea rows={4} className="cs-input mt-2" value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Ex : le logo doit rester en bas à droite, ne pas recadrer la photo…" data-testid="format-notes" />
                </div>

                <div className="mt-6 border border-black/15 bg-white/70 p-4 text-[13px] text-neutral-700">
                  Prix calculé pour une adaptation simple de votre création existante. Si votre fichier nécessite une recomposition
                  plus importante, vous serez prévenu <span className="text-black">avant toute intervention supplémentaire</span>.
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 className="cs-h3 text-2xl">Votre adaptation</h2>
                <dl className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <Row k="Fichiers reçus" v={files.length ? `${files.length} fichier(s)` : "—"} />
                  <Row k="Fichier source" v={LABELS.has_source[hasSource || "unknown"]} />
                  <Row k="Format actuel" v={currentKnown && current.w && current.h ? `${current.w} × ${current.h} ${current.unit}` : "à vérifier"} />
                  <Row k="Nombre de formats" v={String(formats.length)} />
                  <Row k="Formats recto-verso" v={String(price.rv)} />
                  <Row k="Composition" v={LABELS.composition_intent[compositionIntent]} />
                  {formats.some((f) => f.channel === "print") && (
                    <Row k="Fonds perdus" v="Inclus pour l'impression" />
                  )}
                </dl>

                <div className="mt-6">
                  <div className="cs-eyebrow text-neutral-500">Formats demandés</div>
                  <ul className="mt-3 space-y-2 text-sm">
                    {formats.map((f, i) => {
                      const pool = f.channel === "print" ? PRINT_PRESETS : DIGITAL_PRESETS;
                      const p = pool.find((x) => x.key === f.preset);
                      const dim = p && p.key !== "custom" ? p.label : `${f.width || "?"}×${f.height || "?"} ${f.unit}`;
                      return (
                        <li key={i} className="flex justify-between border-b border-black/10 pb-2">
                          <span>Format {i + 1} — {LABELS.channel[f.channel]} — {dim}</span>
                          <span className="font-mono text-neutral-600">{f.rv ? "recto-verso" : "recto"}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="mt-6 border-t border-black/10 pt-4 flex items-end justify-between">
                  <div>
                    <div className="cs-eyebrow text-neutral-500">Prix</div>
                    <div className="font-serif text-4xl">{eur(price.total)}</div>
                    <div className="text-[12px] font-mono text-neutral-500 mt-1">
                      Base {eur(price.base)} · Recto-verso {price.surcharge ? `+${eur(price.surcharge)}` : "0 €"}
                    </div>
                  </div>
                </div>

                <div className="mt-8 grid sm:grid-cols-2 gap-6">
                  <div>
                    <div className="cs-eyebrow text-neutral-500">Ce qui est inclus</div>
                    <ul className="mt-3 space-y-1.5 text-[14px]">
                      {["Adaptation aux dimensions demandées", "Ajustements mineurs nécessaires", "Résolution et colorimétrie adaptées", "Fonds perdus si nécessaires", "Export final", "Contrôle du rendu", "1 ajustement technique mineur"].map((it) => (
                        <li key={it} className="flex items-start gap-2"><Check size={14} className="mt-1 text-[#0E7C90] shrink-0" /><span>{it}</span></li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="cs-eyebrow text-neutral-500">Non inclus automatiquement</div>
                    <ul className="mt-3 space-y-1.5 text-[14px] text-neutral-600">
                      {["Création d'un nouveau design", "Refonte graphique importante", "Reconstruction complexe", "Retouche photo importante", "Création d'éléments manquants"].map((it) => (
                        <li key={it}>· {it}</li>
                      ))}
                    </ul>
                    <p className="mt-3 text-[12px] font-mono text-neutral-500">Si nécessaire : estimation préalable · aucun supplément sans votre accord.</p>
                  </div>
                </div>

                <label className="mt-8 flex items-start gap-2 text-[14px]">
                  <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="format-confirm" />
                  <span>Je confirme que les formats et dimensions indiqués correspondent à ma demande.</span>
                </label>

                <button onClick={submit} disabled={!confirm || submitting} className="cs-btn-primary mt-6 w-full sm:w-auto justify-center disabled:opacity-50" data-testid="format-submit">
                  {submitting ? "Envoi…" : "Valider ma demande"} <ArrowUpRight size={16} />
                </button>
              </div>
            )}
          </div>

          {step < 4 && (
            <div className="mt-6 flex items-center justify-between">
              <button onClick={prev} disabled={step === 0} className="text-sm cs-link-underline disabled:opacity-40" data-testid="format-prev">← Retour</button>
              <button onClick={next} disabled={!canNext()} className="cs-btn-primary disabled:opacity-50" data-testid="format-next">Continuer <ArrowUpRight size={18} /></button>
            </div>
          )}

          {/* Passerelles */}
          <div className="mt-10 border-t border-black/10 pt-6 text-[13px] text-neutral-600 space-y-2">
            <div><span className="text-neutral-500">Votre visuel n'existe pas encore ?</span> <Link to="/creer-mon-support" className="cs-link-underline text-[#0E7C90]">→ Créer mon support</Link></div>
            <div><span className="text-neutral-500">Votre imprimeur impose des spécifications techniques ?</span> <Link to="/configurer/conforme" className="cs-link-underline text-[#0E7C90]">→ Rendre mon fichier conforme à l'imprimeur</Link></div>
            <div><span className="text-neutral-500">Votre fichier est refusé par l'imprimeur ?</span> <Link to="/sos-print" className="cs-link-underline text-[#0E7C90]">→ SOS Print</Link></div>
            <div><span className="text-neutral-500">Plusieurs éléments sur une planche prête à découper ?</span> <Link to="/configurer/planche" className="cs-link-underline text-[#0E7C90]">→ Créer une planche PDF</Link></div>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="cs-card p-6 sticky top-24" data-testid="format-price-panel">
            <div className="cs-eyebrow text-neutral-500">Votre commande</div>
            <div className="mt-3 font-serif text-2xl">Le mettre au bon format</div>

            <div className="mt-4 border-t border-black/10 pt-3 text-sm">
              <div className="flex justify-between">
                <span>{formats.length} nouveau{formats.length > 1 ? "x" : ""} format{formats.length > 1 ? "s" : ""}</span>
                <span className="font-mono">{eur(price.base)}</span>
              </div>
              {price.rv > 0 && (
                <div className="flex justify-between mt-1">
                  <span>{price.rv} format{price.rv > 1 ? "s" : ""} recto-verso</span>
                  <span className="font-mono">+ {eur(price.surcharge)}</span>
                </div>
              )}
            </div>

            <div className="mt-4 border-t border-black/10 pt-3 flex justify-between items-baseline">
              <span className="cs-eyebrow text-neutral-500">Total</span>
              <span className="font-serif text-3xl" data-testid="format-total">{eur(price.total)}</span>
            </div>

            <p className="mt-3 text-[12px] font-mono text-neutral-500">Prix calculé pour une adaptation simple.</p>
            <ul className="mt-3 text-[12px] font-mono text-neutral-500 space-y-1">
              <li>Fonds perdus inclus</li>
              <li>Contrôle & export inclus</li>
              <li>1 ajustement mineur inclus</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-black/10 pb-2">
      <dt className="text-neutral-500 font-mono text-xs uppercase tracking-widest">{k}</dt>
      <dd className="text-right">{v || "—"}</dd>
    </div>
  );
}
